import {ConfirmedLineEditor} from "./confirmedLineEditor";
import {canEditConfirmedOrder} from "../../../utils/orderState";
import {CartLine} from "./cartLine";
import {LineEditor} from "./lineEditor";
import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useSelector, useDispatch } from "react-redux";

import {useCartAmounts} from "../../../hooks/useCartAmounts";
import { StateLabel } from "@delibrandev/utils";

import { LuShoppingCart } from "react-icons/lu";
import { OrderHeader } from "./header";
import { OrderFooter } from "./orderFooter";

import {
  initRequestPayments,
  requestOrderCheckout,
  setAlert,
  updateCart,
  updateCarts,
  updateOrder,
  updateRequestIsOrder,
  updateRequestIsRecoveredCart,
} from "../../../reducers/userConfigSlice";

import { getCustomerCarts } from "../../../api/cart";
import { getOrderDetails } from "../../../api/orders";
import {
  buildSyncResourceKey,
  flushAllProductChanges,
  getProductSyncSnapshotVersion,
  getProductSyncStatus,
  hasAnyPendingProductChanges,
  hasPendingProductChanges,
  resumeProductChanges,
  subscribeProductSync,
} from "../../../services/productSyncQueue";

import { BsTrash } from "react-icons/bs";

import "./../style.css";

export const OrderInfo = ({ onOrderConfirmed }) => {
  const dispatch = useDispatch();

  const attributes = useSelector((state) => state.aplicationConfig.corporationData.attributes);
  const options = useSelector((state) => state.aplicationConfig.corporationData.options);
  const order = useSelector((state) => state.aplicationConfig.userConfig.order);
  const orderProducts = useSelector((state) => state.aplicationConfig.userConfig.order.products);
  const client = useSelector((state) => state.aplicationConfig.userConfig.client);
  const cart = useSelector((state) => state.aplicationConfig.userConfig.cart);
  const requestIsOrder = useSelector((state) => state.aplicationConfig.userConfig.request.isOrder);
  const isRecoveredCart = useSelector((state) => state.aplicationConfig.userConfig.request.isRecoveredCart);

  const [showCartPayments, setShowCartPayments] = useState(false);
  const checkoutOrderId = useSelector(state=>state.aplicationConfig.userConfig.request.checkoutOrderId);
  const [editingLine,setEditingLine]=useState(null);
  const initializedRef = useRef(false);
  const currentOrderRef = useRef(order);
  const currentClientRef = useRef(client);
  currentClientRef.current = client;
  const syncVersion = useSyncExternalStore(
    subscribeProductSync,
    getProductSyncSnapshotVersion,
    getProductSyncSnapshotVersion,
  );

  const syncResourceKey = useMemo(() => {
    if (order?.orderId) return buildSyncResourceKey("order", order.orderId);
    const cartKey = cart?.shoppingCartId ?? (client?.customerId ? `new-${client.customerId}` : null);
    return buildSyncResourceKey("cart", cartKey);
  }, [cart?.shoppingCartId, client?.customerId, order?.orderId]);
  const syncStatus = useMemo(() => getProductSyncStatus(syncResourceKey), [syncResourceKey, syncVersion]);

  const [showFloatPanel, setShowFloatPanel] = useState(false);
  const [floatPanelContent, setFloatPanelContent] = useState(<></>);

  const getQuery = useCallback(async () => {
    try {
      if (!client?.customerId) return;

      const payload = { customerId: client.customerId };
      dispatch(updateRequestIsOrder(false));

      const response = await getCustomerCarts(payload);
      if (response && !response.error && !hasAnyPendingProductChanges() && currentClientRef.current?.customerId === client.customerId && !currentOrderRef.current?.orderId) {
        dispatch(updateCarts(response?.data));
      }
    } catch (error) {
      console.error("[OrderInfo] Error en getCustomerCarts:", error);
    }
  }, [client?.customerId, dispatch]);

  const clearCart = useCallback(async () => {
    const synchronized = await flushAllProductChanges();
    if (!synchronized) {
      dispatch(
        setAlert({ called: true, title: "Sincronización", message: "No se puede vaciar: todavía hay cambios sin guardar." }),
      );
      return false;
    }
    dispatch(updateRequestIsOrder(false));
    dispatch(updateRequestIsRecoveredCart(false));
    dispatch(updateOrder({ orderId: null }));
    dispatch(updateCarts([]));
    dispatch(
      updateCart({
        ShoppingCartProducts: [],
        total: 0,
      }),
    );
    return true;
  }, [dispatch]);

  useEffect(() => {
    resumeProductChanges(syncResourceKey);
  }, [syncResourceKey]);

  const transformOrderProductsToCartProducts = useCallback(() => {
    const safeProducts = Array.isArray(orderProducts) ? orderProducts : [];

    return {
      ShoppingCartProducts: safeProducts.map((item) => ({
        Product: {
          id: item.productId ?? item.id ?? undefined,
          name: item.shortName ?? item.name ?? "",
          shortName: item.shortName ?? item.Product?.shortName ?? item.name ?? "",
          description: item.description ?? "",
          price: typeof item.price === "number" ? item.price : Number(item.price) || 0,
          tax: item.tax ?? item.Product?.tax ?? null,
          images: Array.isArray(item.images) ? item.images : [],
        },
        id: item.orderProductId,
        info: [item.variantName,...(item.items || []).map(value=>value.name)].filter(Boolean).join(" · "),
        orderProductId: item.orderProductId,
        status: item.status,
        quantity: item.quantity ?? 1,
        note: item.note ?? item.notes ?? null,
        itemIds: Array.isArray(item.itemIds) ? item.itemIds : [],
        variantId: item.variantId ?? null,
        productId: item.productId ?? null,
      })),
    };
  }, [orderProducts]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Al abrir un pedido o carrito distinto no pueden mantenerse importes
    // introducidos en la operación anterior.
    dispatch(initRequestPayments());

    const hasRecoveredOrder = requestIsOrder === true && Boolean(order?.orderId);
    const hasRecoveredCart = isRecoveredCart === true && Boolean(cart?.shoppingCartId);

    if (hasRecoveredOrder || hasRecoveredCart) {
      if (hasRecoveredOrder) dispatch(updateRequestIsOrder(true));
      setShowCartPayments(false);
      return;
    }

    dispatch(updateOrder({ orderId: null }));
    dispatch(updateRequestIsOrder(false));
    setShowCartPayments(false);
    getQuery();
  }, [cart?.shoppingCartId, dispatch, getQuery, isRecoveredCart, order?.orderId, order?.paymentStatus, requestIsOrder]);

  useEffect(()=>{if(checkoutOrderId && checkoutOrderId===order?.orderId && requestIsOrder){setShowCartPayments(true);dispatch(requestOrderCheckout(null));}},[checkoutOrderId,order?.orderId,requestIsOrder,dispatch]);

  useEffect(() => {
    currentOrderRef.current = order;
  }, [order]);

  useEffect(() => {
    if (!order?.orderId) return undefined;

    let active = true;
    let requestInProgress = false;
    const refreshActiveOrder = async () => {
      const orderResourceKey = buildSyncResourceKey("order", order.orderId);
      if (requestInProgress || hasPendingProductChanges(orderResourceKey)) return;
      requestInProgress = true;
      try {
        const response = await getOrderDetails(order.orderId);
        if (response && !response.error) {
          if (hasPendingProductChanges(orderResourceKey)) return;
          const nextOrder = response?.data?.order ?? response?.data;
          const currentOrder = currentOrderRef.current;
          if (!active || nextOrder?.orderId !== order.orderId || currentOrder?.orderId !== order.orderId) return;

          // Evita sustituir el pedido en Redux cuando el backend devuelve
          // exactamente los mismos datos. Esto reduce renders globales
          // innecesarios durante la edición de un producto.
          if (JSON.stringify(nextOrder) !== JSON.stringify(currentOrder)) {
            dispatch(updateOrder(nextOrder));
          }

          dispatch(updateRequestIsOrder(true));
        }
      } catch (error) {
        console.error("[OrderInfo] Error actualizando pedido activo en background:", error);
      } finally {
        requestInProgress = false;
      }
    };

    const intervalId = window.setInterval(refreshActiveOrder, 10000);
    return () => { active = false; window.clearInterval(intervalId); };
  }, [dispatch, order?.orderId]);

  const cartData = useMemo(() => {
    const currentCart = order?.orderId !== null ? transformOrderProductsToCartProducts() : cart;

    if (!currentCart) return currentCart;

    const cartWithInfo = {
      ...currentCart,

      ShoppingCartProducts: (currentCart.ShoppingCartProducts ?? []).map((product) => {
        const info = order?.orderId ? [product.info].filter(Boolean) : [];

        const itemIds = Array.isArray(product?.itemIds) ? product.itemIds : [];

        if(!order?.orderId) itemIds.forEach((itemId) => {
          const item = attributes?.flatMap((attribute) => attribute?.items ?? []).find((item) => item?.itemId === itemId);

          if (item?.name) {
            info.push(item.name);
          }
        });

        if (!order?.orderId && product?.variantId) {
          const option = options?.find(
            (option) =>
              option?.variantId === product.variantId ||
              option?.optionId === product.variantId ||
              option?.id === product.variantId,
          );

          if (option?.name) {
            info.push(option.name);
          }
        }

        const note = Array.isArray(product?.note)
          ? product.note
              .map((entry) => (typeof entry === "string" ? entry : entry?.note ?? entry?.text ?? entry?.value ?? ""))
              .filter(Boolean)
              .join(" · ")
              .trim()
          : typeof product?.note === "string"
            ? product.note.trim()
            : "";

        return {
          ...product,
          info: `${info.join(" · ")}${note ? `\n${note}` : ""}`,
        };
      }),
    };

    return cartWithInfo;
  }, [order?.orderId, transformOrderProductsToCartProducts, cart, attributes, options]);

  const isOrder = Boolean(order?.orderId);
  const canEditProducts = !isOrder || (canEditConfirmedOrder(order));

  const quote = useCartAmounts();
  const pricedLines = order?.orderId ? order.products : (quote.amounts || quote.displayAmounts)?.lines;
  const totalQuantity = (cartData?.ShoppingCartProducts || []).reduce((count,line)=>count+(Number(line.quantity)||0),0);
  const lineKey = line => JSON.stringify([line.productId,line.variantId || "",[...(line.itemIds || [])].sort(),line.note || ""]);

  return (
    <>
      <div
        className={`order-commander-container ${showCartPayments && "order-commander-container-payments"} ${!canEditProducts && "order-commander-container-noEdit"}`}
      >
        <OrderHeader setShowFloatPanel={setShowFloatPanel} setFloatPanelContent={setFloatPanelContent} />

        <div className="flex p-2 tpvOrderCaption">
          <div>
            <b>{isOrder ? `Pedido #${order.displayNumber || order.number || ""}` : "Pedido actual"}</b>
          </div>

          <span className="tpvOrderQuantity">{totalQuantity} {totalQuantity === 1 ? "artículo" : "artículos"}</span>
          {order?.status && <StateLabel state={order?.status} text={""} />}

          {(syncStatus.pendingCount > 0 || syncStatus.syncing || syncStatus.error) && (
            <div className={`product-sync-status ${syncStatus.error ? "product-sync-status-error" : ""}`}>
              {syncStatus.error ? "Cambios pendientes · revisar" : "Guardando cambios…"}
            </div>
          )}

          {!isOrder && canEditProducts && cartData?.ShoppingCartProducts?.length > 0 && (
            <button type="button" aria-label="Vaciar pedido"
              style={{ color: "var(--color-error)", cursor: "pointer" }}
              onClick={clearCart}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clearCart();
                }
              }}
            >
              <BsTrash size={18} />
            </button>
          )}
        </div>

        <div className="order-commander-shopping-cart">
          <div className="orderShoppingCart orderShoppingCartCompact">{!cartData?.ShoppingCartProducts?.length && <div className="tpvEmptyOrder"><LuShoppingCart aria-hidden="true" /><strong>Pedido vacío</strong><p>Toca un producto para añadirlo al pedido.</p></div>}{(cartData?.ShoppingCartProducts || []).map((item, index) => {
            const priced = pricedLines?.find(line => lineKey(line) === lineKey(item));
            return <CartLine key={item.id || item.shoppingCartProductId || index} item={item} amount={priced?.lineTotalWithTax} editable={canEditProducts} busy={syncStatus.pendingCount > 0 || syncStatus.syncing || Boolean(syncStatus.error)} onEdit={setEditingLine} />;
          })}</div>
        </div>

        <OrderFooter
          setShowCartPayments={setShowCartPayments}
          showCartPayments={showCartPayments}
          clearCart={clearCart}
          cartData={cartData}
          onOrderConfirmed={onOrderConfirmed}
        />
      </div>

      {editingLine && (isOrder ? <ConfirmedLineEditor line={editingLine} onClose={()=>setEditingLine(null)}/> : <LineEditor line={editingLine} onClose={()=>setEditingLine(null)}/>) }
      {showFloatPanel && floatPanelContent}
    </>
  );
};
